<!--
  Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="chat-input">
    <div class="chat-input__container">
      <!-- File upload button -->
      <PrimeButton
        type="button"
        icon="ri-attachment-line"
        text
        rounded
        :disabled="disabled"
        :aria-label="$t('chat.attachFile')"
        @click="triggerFileInput"
      />
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        multiple
        @change="handleFileSelect"
      />

      <!-- Text input -->
      <PrimeTextarea
        v-model="message"
        :placeholder="$t('chat.typeMessage')"
        :disabled="disabled"
        auto-resize
        :rows="1"
        class="chat-input__textarea"
        @keydown="handleKeyDown"
        @input="handleInput"
      />

      <!-- Send button -->
      <PrimeButton
        type="button"
        icon="ri-send-plane-2-fill"
        rounded
        :disabled="disabled || !canSend"
        :aria-label="$t('chat.send')"
        @click="sendMessage"
      />
    </div>

    <!-- Typing indicator -->
    <div v-if="typingUsers.length > 0" class="chat-input__typing">
      <span class="typing-dots">
        <span></span>
        <span></span>
        <span></span>
      </span>
      <span class="typing-text">
        {{ typingText }}
      </span>
    </div>

    <!-- File preview -->
    <div v-if="selectedFiles.length > 0" class="chat-input__files">
      <div
        v-for="(file, index) in selectedFiles"
        :key="index"
        class="chat-input__file"
      >
        <i class="ri-file-line" aria-hidden="true"></i>
        <span class="file-name">{{ file.name }}</span>
        <PrimeButton
          type="button"
          icon="ri-close-line"
          :aria-label="$t('delete')"
          text
          rounded
          size="small"
          severity="danger"
          @click="removeFile(index)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Button from "primevue/button";
import Textarea from "primevue/textarea";

export default {
  name: "ChatInput",
  components: {
    PrimeButton: Button,
    PrimeTextarea: Textarea,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    typingUsers: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["send", "typing", "upload"],
  data() {
    return {
      message: "",
      selectedFiles: [],
      typingTimeout: null,
      isTyping: false,
    };
  },
  computed: {
    canSend() {
      return this.message.trim().length > 0 || this.selectedFiles.length > 0;
    },
    typingText() {
      if (this.typingUsers.length === 0) {
        return "";
      }
      if (this.typingUsers.length === 1) {
        return this.$t("chat.userTyping", { user: this.typingUsers[0] });
      }
      if (this.typingUsers.length === 2) {
        return this.$t("chat.usersTyping", {
          user1: this.typingUsers[0],
          user2: this.typingUsers[1],
        });
      }
      return this.$t("chat.multipleUsersTyping");
    },
  },
  beforeUnmount() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    if (this.isTyping) {
      this.$emit("typing", false);
    }
  },
  methods: {
    handleKeyDown(event) {
      // Send on Enter (without Shift)
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },
    handleInput() {
      // Send typing indicator
      if (!this.isTyping) {
        this.isTyping = true;
        this.$emit("typing", true);
      }

      // Reset typing timeout
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }

      // Stop typing after 3 seconds of inactivity
      this.typingTimeout = setTimeout(() => {
        if (this.isTyping) {
          this.isTyping = false;
          this.$emit("typing", false);
        }
      }, 3000);
    },
    sendMessage() {
      if (!this.canSend) {
        return;
      }

      // Stop typing indicator
      if (this.isTyping) {
        this.isTyping = false;
        this.$emit("typing", false);
      }

      // Upload files first
      if (this.selectedFiles.length > 0) {
        this.selectedFiles.forEach((file) => {
          this.$emit("upload", file, this.message);
        });
        this.selectedFiles = [];
        this.message = "";
        return;
      }

      // Send text message
      if (this.message.trim()) {
        this.$emit("send", this.message.trim());
        this.message = "";
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.selectedFiles = [...this.selectedFiles, ...files];
      // Reset input for re-selection of same file
      event.target.value = "";
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
  },
};
</script>

<style lang="css" scoped>
.chat-input {
  border-top: 1px solid var(--p-surface-border);
  padding: 0.5rem;
  background-color: var(--p-surface-card);
}

.chat-input__container {
  display: flex;
  align-items: flex-end;
  gap: 0.25rem;
}

.chat-input__textarea {
  flex: 1;
  max-height: 120px;
  resize: none;
}

.chat-input__textarea :deep(textarea) {
  border-radius: 1.25rem;
}

.chat-input__typing {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.typing-dots {
  display: flex;
  gap: 2px;
}

.typing-dots span {
  width: 4px;
  height: 4px;
  background-color: var(--text-color-secondary);
  border-radius: 50%;
  animation: typing-bounce 1.4s infinite ease-in-out both;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing-bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.chat-input__files {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem;
}

.chat-input__file {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background-color: var(--surface-100);
  border-radius: 0.5rem;
  font-size: 0.85rem;
}

.chat-input__file i {
  color: var(--text-color-secondary);
}

.file-name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hidden {
  display: none;
}
</style>
